import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getStoredAuth, setStoredAuth } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(getStoredAuth());
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  // Bumps on every successful refreshState — used by RandomEventModal
  // to re-poll /event/check after battles resolve.
  const [battleTick, setBattleTick] = useState(0);

  const refreshState = useCallback(async () => {
    if (!getStoredAuth()) return;
    try {
      const { data } = await api.get("/game/state");
      setState(data);
      setBattleTick((t) => t + 1);
    } catch (e) {
      if (e?.response?.status === 401) {
        setStoredAuth(null);
        setAuth(null);
        setState(null);
      }
    }
  }, []);

  useEffect(() => {
    if (auth) refreshState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  const login = async (email, password, rememberMe = true) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      setStoredAuth(data, rememberMe);
      setAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Login failed" };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, callsign, rememberMe = true) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register",
        { email, password, callsign, remember_me: rememberMe });
      setStoredAuth(data, rememberMe);
      setAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Registration failed" };
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogleSession = async (sessionId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      setStoredAuth(data, true);
      setAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Google sign-in failed" };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    setStoredAuth(null);
    setAuth(null);
    setState(null);
  };

  return (
    <AuthCtx.Provider value={{ auth, state, loading, login, register, loginWithGoogleSession, logout, refreshState, setState, battleTick }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
